import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminPageShell from '../components/AdminPageShell';
import AdminTabs from '../components/AdminTabs';
import CyclesView from './elections/CyclesView';
import OfficesView from './elections/OfficesView';
import InitiativesView from './elections/InitiativesView';
import LocationsView from './elections/LocationsView';
import MilestoneTypesView from './elections/MilestoneTypesView';

const TABS = [
  { key: 'cycles', label: 'Election Cycles' },
  { key: 'offices', label: 'Offices' },
  { key: 'milestone-types', label: 'Milestone Types' },
  { key: 'initiatives', label: 'Ballot Initiatives' },
  { key: 'locations', label: 'Voting Locations' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Elections — election cycles (the container for everything electoral), the
 * shared office catalog, the milestone type vocabulary, ballot initiatives, and
 * voting locations. Contests are
 * managed inside each cycle's detail page, never as a standalone list.
 * Plans: Documentation/ELECTIONS_TOOL_PLAN.md,
 *        Documentation/VOTING_LOCATIONS_ADMIN_TOOL_PRD.md
 */
const ElectionsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = TABS.some((tab) => tab.key === requestedTab)
    ? (requestedTab as TabKey)
    : 'cycles';

  return (
    <AdminPageShell
      title="Elections"
      description="Election cycles, the shared office catalog, contests, and ballot initiatives"
      showHeading={false}
    >
      <AdminTabs
        tabs={[...TABS]}
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
      />

      {activeTab === 'cycles' && <CyclesView />}
      {activeTab === 'offices' && <OfficesView />}
      {activeTab === 'milestone-types' && <MilestoneTypesView />}
      {activeTab === 'initiatives' && <InitiativesView />}
      {activeTab === 'locations' && <LocationsView />}
    </AdminPageShell>
  );
};

export default ElectionsPage;
